import { useRef, useState, useEffect } from "react";
import CustomIcon from "../CustomIcon/CustomIcon";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import it from "date-fns/locale/it";
import { formatTime } from "@/utils";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { LanguageAtom } from "../../atoms";

const CustomTimePicker: React.FC<any> = ({
  isParking = false,
  colorArrow = "#505050",
  isError = false,
  selected,
  ...props
}) => {
  const pickerRef = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const lang = useRecoilValue(LanguageAtom);
  const { t } = useTranslation();

  if (lang === "it") registerLocale(lang, it);

  useEffect(() => {
    if (!isOpen) return;

    const timeout = setTimeout(() => {
      const items = document.querySelectorAll<HTMLLIElement>(
        ".react-datepicker__time-list-item"
      );

      if (items.length) {
        items.forEach((item) => {
          item.setAttribute("tabindex", "0");

          // const clickHandler = () => {
          //   const timeText = item.textContent?.trim();
          //   if (!timeText) return;

          //   const [hours, minutes] = timeText.split(":").map(Number);
          //   const selectedDate = new Date();
          //   selectedDate.setHours(hours, minutes, 0, 0);

          //   props.onChange(selectedDate);
          //   setIsOpen(false);
          // };
          const clickHandler = () => {
            const timeText = item.textContent?.trim();
            if (!timeText) return;

            const [hours, minutes] = timeText.split(":").map(Number);
            const selectedDate = selected ? new Date(selected) : new Date(); 
            selectedDate.setHours(hours, minutes, 0, 0);

            props.onChange(selectedDate);
            setIsOpen(false);
          };

          item.addEventListener("click", clickHandler);
          item.dataset.listener = "true";

          item.addEventListener("cleanup", () =>
            item.removeEventListener("click", clickHandler)
          );
        });

        const selectedEl = document.querySelector<HTMLLIElement>(
          ".react-datepicker__time-list-item--selected"
        );
        if (selectedEl) selectedEl.focus();

        const handleKeyDown = (e: KeyboardEvent) => {
          const focused = document.activeElement as HTMLElement;
          const index = Array.from(items).indexOf(focused as HTMLLIElement);

          if (e.key === "ArrowDown" && index < items.length - 1) {
            e.preventDefault();
            items[index + 1].focus();
          }

          if (e.key === "ArrowUp" && index > 0) {
            e.preventDefault();
            items[index - 1].focus();
          }

          if (e.key === "Enter") {
            e.preventDefault();
            focused.click();
            setIsOpen(false);
          }

          if (e.key === "Escape") {
            setIsOpen(false);
          }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
          document.removeEventListener("keydown", handleKeyDown);
          items.forEach((item) => {
            const cleanup = new Event("cleanup");
            item.dispatchEvent(cleanup);
          });
        };
      }
    }, 0);

    return () => clearTimeout(timeout);
  }, [isOpen, props]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.key === "Enter" || e.key === " ") && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
    if (e.key === "Escape" && isOpen) {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className="h-full w-full" onClick={(e) => e.stopPropagation()}>
      <div
        onClick={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className={`bg-white py-3 px-6 h-full cursor-pointer rounded-2xl desk:rounded-r-2xl ${
          isError ? "border border-red" : ""
        } flex gap-4 items-center justify-between`}
        role="button"
        aria-label={t("parking.time") ?? ""}
        tabIndex={0}
      >
        <div className="flex items-center justify-between w-full gap-2">
          <span>{selected ? formatTime(selected) : t("parking.hour")}</span>

          {!isParking ? (
            selected &&
            selected.getHours() + ":" + selected.getMinutes() !==
              new Date().getHours() + ":" + new Date().getMinutes() ? (
              <button
                type="button"
                aria-label={t("globalSearch.delete") as string}
                onClick={(e) => {
                  e.stopPropagation();
                  props.onChange(new Date());
                }}
                className="z-[5] text-black focus:outline-none"
              >
                x
              </button>
            ) : (
              <CustomIcon
                className={`w-2 transition-transform origin-center duration-300 ${
                  isOpen ? "rotate-90" : "rotate-0"
                }`}
                iconName="chevron-r"
                color={colorArrow}
              />
            )
          ) : (
            <CustomIcon
              className={`w-2 transition-transform origin-center duration-300 ${
                isOpen ? "rotate-90" : "rotate-0"
              }`}
              iconName="chevron-r"
              color={colorArrow}
            />
          )}
        </div>
      </div>

      <div className="overwritedPickerContainer">
        <ReactDatePicker
          ref={pickerRef}
          locale={lang === "it" ? "it" : ""}
          timeInputLabel={t("parking.time") + ":"}
          timeCaption={t("parking.time")}
          selected={selected}
          timeFormat="HH:mm"
          onChange={(e: Date) => {
            setIsOpen(false);
            props.onChange(e);
          }}
          onClickOutside={() => setIsOpen(false)}
          open={isOpen}
          showTimeSelect
          showTimeSelectOnly
          {...props}
        />
      </div>
    </div>
  );
};

export default CustomTimePicker;
